import { useState, useRef, useEffect } from 'react';
import { Film, Search, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { debouncedSearchMovies, type MovieSearchResult } from '../../lib/tmdb';

interface Props {
  onSelect: (movie: MovieSearchResult) => void;
  placeholder?: string;
  className?: string;
}

export function MovieSearch({ onSelect, placeholder = 'Search for a movie...', className }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MovieSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = async (searchQuery: string) => {
    setQuery(searchQuery);
    setError(null);

    if (!searchQuery.trim()) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setLoading(true);
    setIsOpen(true);

    try {
      const searchResults = await debouncedSearchMovies(searchQuery);
      setResults(searchResults);
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to search movies. Please try again.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (movie: MovieSearchResult) => {
    onSelect(movie);
    setQuery('');
    setResults([]);
    setIsOpen(false);
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setError(null);
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
          <Search className="h-5 w-5 text-muted-foreground/60" />
        </div>
        <input
          type="text"
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder={placeholder}
          className={cn(
            'input-dark pl-11 pr-11',
            error && '!border-destructive/50'
          )}
        />
        {query && (
          <button
            onClick={clearSearch}
            className="absolute inset-y-0 right-0 flex items-center pr-4"
            aria-label="Clear search"
          >
            <X className="h-5 w-5 text-muted-foreground/60 transition-colors hover:text-foreground" />
          </button>
        )}
      </div>

      {error && (
        <p className="mt-2 text-sm text-red-400">{error}</p>
      )}

      {isOpen && (
        <div className="absolute z-20 mt-2 max-h-96 w-full overflow-y-auto rounded-2xl border border-white/10 bg-popover shadow-deep">
          {loading ? (
            <div className="p-5 text-center">
              <div className="mx-auto h-1 w-32 overflow-hidden rounded-full bg-secondary">
                <div className="animate-progress h-full rounded-full bg-primary" />
              </div>
            </div>
          ) : results.length > 0 ? (
            <ul className="py-2">
              {results.map((movie) => (
                <li key={movie.id}>
                  <button
                    onClick={() => handleSelect(movie)}
                    className="flex w-full items-start gap-3.5 px-4 py-2.5 text-left transition-colors hover:bg-white/[0.05]"
                  >
                    <div className="poster-frame h-16 w-11 shrink-0 !rounded-md">
                      {movie.posterPath ? (
                        <img
                          src={movie.posterPath}
                          alt={movie.title}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center">
                          <Film className="h-5 w-5 text-muted-foreground/50" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="truncate font-medium text-foreground">{movie.title}</span>
                        {movie.year > 0 && (
                          <span className="shrink-0 text-sm text-muted-foreground">{movie.year}</span>
                        )}
                      </div>
                      <p className="mt-0.5 line-clamp-2 text-sm leading-snug text-muted-foreground">
                        {movie.overview}
                      </p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : query.trim() ? (
            <div className="p-5 text-center text-sm text-muted-foreground">
              No movies found
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
